
import React from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { EnhancedColorResult, ColorFeedback } from "@/hooks/color-check/types";
import EmptyResultState from "./result/EmptyResultState";
import { ResultHeader } from "./result/ResultHeader";
import ProductAnalysisSection from "./result/ProductAnalysisSection";
import BestMatchSection from "./result/BestMatchSection";
import BasePredictionSection from "./result/BasePredictionSection";
import InventoryMatchesList from "./result/InventoryMatchesList";
import LearningMetricsSection from "./result/LearningMetricsSection";
import ResultFooter from "./result/ResultFooter";

interface EnhancedColorCheckResultProps {
  colorResult: EnhancedColorResult | null;
  onSendExternalRequest: () => void;
  onProvideFeedback: (feedback: ColorFeedback) => void;
}

const EnhancedColorCheckResult = ({ 
  colorResult, 
  onSendExternalRequest,
  onProvideFeedback 
}: EnhancedColorCheckResultProps) => {
  if (!colorResult) {
    return <EmptyResultState />;
  }

  const hasInventoryMatches = colorResult.inventoryMatches.length > 0;
  const hasExactMatches = colorResult.learningMetrics.exactMatchesFound && colorResult.learningMetrics.exactMatchesFound > 0;
  const hasExactSizeMatches = colorResult.learningMetrics.exactSizeMatchesFound && colorResult.learningMetrics.exactSizeMatchesFound > 0;
  const hasExactColorMatches = colorResult.inventoryMatches.filter(m => m.colorMatch).length > 0;
  const bestMatch = colorResult.inventoryMatches[0];

  const getDescription = () => {
    if (hasExactColorMatches && hasExactSizeMatches) {
      return 'Perfecte kleur en maat match gevonden!';
    } else if (hasExactColorMatches) {
      return 'Juiste kleur gevonden - andere maat beschikbaar';
    } else if (hasExactSizeMatches) {
      return 'Juiste maat gevonden - verkeerde kleur';
    } else {
      return 'Slimme analyse van:';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <ResultHeader 
            colorResult={colorResult}
            hasExactMatches={hasExactMatches}
            hasExactSizeMatches={hasExactSizeMatches}
            hasExactColorMatches={hasExactColorMatches}
            hasInventoryMatches={hasInventoryMatches}
          />
        </CardTitle>
        <CardDescription>
          {getDescription()} {colorResult.parsedInfo.colorCode || colorResult.originalInput}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Enhanced Parsed Information with COLOR Priority */}
        <ProductAnalysisSection parsedInfo={colorResult.parsedInfo} />

        <Separator />

        {/* Enhanced Best Match with COLOR Information */}
        {bestMatch && (
          <>
            <BestMatchSection 
              bestMatch={bestMatch}
              parsedInfo={colorResult.parsedInfo}
            />
            <Separator />
          </>
        )}

        {/* Enhanced Base Color Prediction */}
        <BasePredictionSection 
          basePrediction={colorResult.basePrediction}
          hasExactColorMatches={hasExactColorMatches}
        />

        <Separator />

        {/* Enhanced Inventory Matches with COLOR Priority */}
        <InventoryMatchesList 
          inventoryMatches={colorResult.inventoryMatches}
          hasInventoryMatches={hasInventoryMatches}
          hasExactColorMatches={hasExactColorMatches}
        />

        <Separator />

        {/* Enhanced Learning Metrics */}
        <LearningMetricsSection learningMetrics={colorResult.learningMetrics} />
      </CardContent>

      <CardFooter className="flex flex-col space-y-2">
        <ResultFooter 
          colorResult={colorResult}
          hasInventoryMatches={hasInventoryMatches}
          hasExactColorMatches={hasExactColorMatches}
          hasExactSizeMatches={hasExactSizeMatches}
          onSendExternalRequest={onSendExternalRequest}
          onProvideFeedback={onProvideFeedback}
        />
      </CardFooter>
    </Card>
  );
};

export default EnhancedColorCheckResult;
